/**
 * useSaveQueue Hook - React integration for SaveQueueManager
 *
 * Provides a React-friendly interface to the background save queue,
 * including state management and save operations.
 *
 * @module features/storage/hooks/useSaveQueue
 * @see SPEC-006-background-cloud-save-queue
 * @see Requirements 8.1-8.6
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  SaveQueueManager,
  createSaveQueueManager,
  SaveQueueManagerOptions,
} from '../saveQueueManager';
import type { SaveQueueState, FlushResult, FlushError } from '../types';

/**
 * Options for useSaveQueue hook
 */
export interface UseSaveQueueOptions {
  /** Base URL for the API */
  baseUrl: string;
  /** Function to get the current auth token */
  getAuthToken: () => Promise<string>;
  /** Debounce delay in milliseconds (default: 2000) */
  debounceMs?: number;
  /** Callback when a version conflict is detected */
  onConflict?: (docId: string, serverVersionId: string) => void;
  /** Callback when an error occurs after all retries */
  onError?: (error: FlushError) => void;
  /** Callback when a save succeeds, provides new versionId */
  onSuccess?: (docId: string, newVersionId: string) => void;
}

/**
 * Return type for useSaveQueue hook
 */
export interface UseSaveQueueReturn {
  /** Current save queue state */
  state: SaveQueueState;
  /** Save document content (debounced) */
  save: (docId: string, markdown: string, baseVersionId: string, meta?: { title?: string }) => void;
  /** Manually trigger immediate flush */
  retryNow: (docId?: string) => Promise<FlushResult[]>;
  /** Clear all pending saves */
  clearQueue: (docId?: string) => Promise<void>;
  /** Whether the manager is initialized */
  isInitialized: boolean;
}

/**
 * Default initial state
 */
const DEFAULT_STATE: SaveQueueState = {
  status: 'saved',
  queueLength: 0,
  lastSavedAt: null,
  lastError: null,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
};

/**
 * useSaveQueue - React hook for background save queue management
 *
 * Encapsulates SaveQueueManager as a React hook, providing reactive state
 * updates and memoized callbacks for save operations.
 *
 * @param options - Configuration options
 * @returns Save queue state and operations
 *
 * @see Requirements 8.1-8.6 - Save status indicator states
 *
 * @example
 * ```tsx
 * function Editor({ docId }: { docId: string }) {
 *   const { state, save, retryNow, clearQueue } = useSaveQueue({
 *     baseUrl: 'https://api.linkode.app',
 *     getAuthToken: () => getToken(),
 *     onConflict: (docId) => showConflictDialog(docId),
 *   });
 *
 *   const handleChange = (markdown: string) => {
 *     save(docId, markdown, currentVersionId);
 *   };
 *
 *   return (
 *     <div>
 *       <SaveStatusIndicator status={state.status} />
 *       <Editor onChange={handleChange} />
 *       {state.status === 'error' && (
 *         <button onClick={() => retryNow()}>Retry</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSaveQueue(options: UseSaveQueueOptions): UseSaveQueueReturn {
  const { baseUrl, getAuthToken, debounceMs, onConflict, onError, onSuccess } = options;

  // State
  const [state, setState] = useState<SaveQueueState>(DEFAULT_STATE);
  const [isInitialized, setIsInitialized] = useState(false);

  // Manager ref (persists across renders)
  const managerRef = useRef<SaveQueueManager | null>(null);

  // Initialize manager on mount
  useEffect(() => {
    const managerOptions: SaveQueueManagerOptions = {
      debounceMs,
      onStatusChange: (newState) => {
        setState(newState);
      },
      onConflict,
      onError,
      onSuccess,
    };

    const manager = createSaveQueueManager(baseUrl, getAuthToken, managerOptions);
    managerRef.current = manager;

    // Initialize asynchronously
    manager
      .init()
      .then(() => {
        setIsInitialized(true);
        setState(manager.getState());
      })
      .catch((err) => {
        console.error('[useSaveQueue] Failed to initialize:', err);
        setState((prev) => ({
          ...prev,
          status: 'error',
          lastError: err instanceof Error ? err.message : 'Failed to initialize',
        }));
      });

    // Cleanup on unmount
    return () => {
      // Flush any pending debounced save before destroying
      manager.flushDebouncer();
      manager.destroy();
      managerRef.current = null;
    };
  }, [baseUrl, getAuthToken, debounceMs, onConflict, onError, onSuccess]);

  // Memoized save function
  const save = useCallback(
    (docId: string, markdown: string, baseVersionId: string, meta?: { title?: string }) => {
      if (!isInitialized || !managerRef.current) {
        // 初始化完成前靜默忽略，避免警告洗版
        return;
      }
      managerRef.current.save(docId, markdown, baseVersionId, meta);
    },
    [isInitialized]
  );

  // Memoized retryNow function
  const retryNow = useCallback(
    async (docId?: string): Promise<FlushResult[]> => {
      if (!isInitialized || !managerRef.current) {
        return [];
      }
      return managerRef.current.retryNow(docId);
    },
    [isInitialized]
  );

  // Memoized clearQueue function
  const clearQueue = useCallback(
    async (docId?: string): Promise<void> => {
      if (!isInitialized || !managerRef.current) {
        return;
      }
      return managerRef.current.clearQueue(docId);
    },
    [isInitialized]
  );

  return {
    state,
    save,
    retryNow,
    clearQueue,
    isInitialized,
  };
}
